import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getPurchaseOrders, getSuppliers, getBranches } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-600",
  received: "bg-green-50 text-green-600",
  cancelled: "bg-red-50 text-red-500",
};

export default function PurchaseOrders() {
  const { t } = useTranslation("purchase_orders");

  const STATUS_OPTIONS = [
    { value: "", label: t("status_option_all", "All statuses") },
    { value: "pending", label: t("status_pending", "Pending") },
    { value: "received", label: t("status_received", "Received") },
    { value: "cancelled", label: t("status_cancelled", "Cancelled") },
  ];

  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchOrders = async () => {
    try {
      const params = {};
      if (supplierFilter) params.supplier_id = supplierFilter;
      if (branchFilter) params.branch_id = branchFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await getPurchaseOrders(token, params);
      setOrders(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchOrders(); }, [supplierFilter, branchFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getSuppliers(token).then(setSuppliers).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getBranches(token).then(setBranches).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const supplierOptions = [{ value: "", label: t("filter_supplier_all_option", "All suppliers") }, ...suppliers.map((s) => ({ value: String(s.id), label: s.name }))];
  const branchOptions = [{ value: "", label: t("filter_branch_all_option", "All branches") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];

  const sortedAndFiltered = useMemo(() => {
    let result = [...orders];
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [orders, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const columns = [
    { key: "code", label: t("col_code", "Code"), className: "text-gray-700 font-mono font-bold" },
    { key: "supplier", label: t("col_supplier", "Supplier"), className: "text-gray-600", render: (row) => row.supplier?.name || `#${row.supplier_id}` },
    { key: "branch", label: t("col_branch", "Branch"), className: "text-gray-600", render: (row) => row.branch?.name || `#${row.branch_id}` },
    { key: "created_at", label: t("col_date", "Date"), className: "text-gray-500", render: (row) => new Date(row.created_at).toLocaleString() },
    { key: "total_amount", label: t("col_total", "Total"), className: "text-gray-800 font-mono font-bold", render: (row) => money(row.total_amount) },
    {
      key: "status",
      label: t("col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${STATUS_STYLES[row.status] || "bg-gray-100 text-gray-500"}`}>
          {t(`status_${row.status}`, row.status)}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.PurchaseOrders size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Purchase Orders")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Orders to buy stock from a supplier — receiving one adds its stock automatically")}</p>
          </div>
        </div>

        <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-purchase-order")} className="w-full sm:w-auto shrink-0">
          {t("btn_add_purchase_order", "New Purchase Order")}
        </Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SelectBox label={t("filter_supplier_label", "Filter by supplier")} options={supplierOptions} value={supplierFilter} onChange={(val) => { setSupplierFilter(val); setCurrentPage(1); }} />
        <SelectBox label={t("filter_branch_label", "Filter by branch")} options={branchOptions} value={branchFilter} onChange={(val) => { setBranchFilter(val); setCurrentPage(1); }} />
        <SelectBox label={t("filter_status_label", "Filter by status")} options={STATUS_OPTIONS} value={statusFilter} onChange={(val) => { setStatusFilter(val); setCurrentPage(1); }} />
      </div>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <Button variant="outline" size="sm" icon={ICONS.PurchaseOrders} onClick={() => navigate(`/purchase-orders/${row.id}`)}>{isMobile ? ` ${t("btn_view", "View")}` : ""}</Button>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.code}</span>
            <h3 className="font-bold text-gray-800">{money(row.total_amount)}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
